import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useToggle } from 'react-use';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { Flex } from '../design-system';
import Loadable from './Loadable';
import { Session } from '../contexts/sessionContext';

const SearchInput = styled.input`
  padding: 8px 16px;
  border-radius: 20px;
  margin-right: 15px;
  border: 1px solid #b5bac8;
`;

const SearchWrapper = styled.div`
  background: #fff;
  position: absolute;
  z-index: 9;
  top: 43px;
  width: 300px;
  border: 1px solid #b5bac8;
  border-radius: 5px;
  overflow: hidden;
`;

const SearchResult = styled.div`
  h1 {
    font-size: 12px;
    padding: 10px;
    margin: 0px;
    background: #b5bac8;
  }
  ul {
    list-style: none;
    margin: 0;
    margin-block-start: 0;
    padding-inline-start: 0;
  }
  li {
    font-size: 12px;
    padding: 10px;
    line-height: 1.3;
  }
  li:nth-child(even) {
    background: #ebf3f7;
  }
  a {
    color: black;
    text-decoration: none;
  }
  small {
    margin-left: 5px;
    opacity: 0.5;
  }
`;

const UniversalSearch = () => {
  const location = useLocation();
  const [searching, isSearching] = useToggle(false);
  const [loading, isLoading] = useToggle(false);
  const [results, setResults] = useState({
    actions: [],
    projects: [],
    clients: [],
    funders: [],
    contacts: [],
    vault: [],
    total: 0,
  });

  const {
    state: {
      user: { permissions },
    },
  } = useContext(Session);

  useEffect(() => {
    isSearching(false);
  }, [location]);

  const handleChange = async (
    selectedValue: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const term = selectedValue.target.value;

    if (term.length < 3) {
      isSearching(false);
      return;
    }

    isSearching(true);
    isLoading(true);
    await axios.get(`/search?term=${term}`).then((data) => {
      setResults(data.data);
    });
    isLoading(false);
  };

  return (
    <Flex
      style={{ position: 'relative' }}
      onBlur={() => setTimeout(() => isSearching(false), 200)}
    >
      <SearchInput
        placeholder="Search"
        onChange={handleChange}
        onFocus={handleChange}
      />
      {searching && (
        <SearchWrapper>
          <Loadable loading={loading}>
            <SearchResult>
              <div>
                {results.projects.length > 0 && (
                  <div>
                    <h1>Projects</h1>
                    <ul>
                      {results.projects.map((r, i) => {
                        return (
                          <li key={i}>
                            <a href={`/projects/edit/${r.id}`}>
                              {r.description}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </SearchResult>
            <SearchResult>
              {results.actions.length > 0 && (
                <div>
                  <h1>Actions</h1>
                  <ul>
                    {results.actions.map((r, i) => {
                      return (
                        <li key={i}>
                          <a href={`/projects/edit/${r.model_id}`}>
                            {r.title}
                            <small>{r.description}</small>
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </SearchResult>
            <SearchResult>
              {results.clients.length > 0 && (
                <div>
                  <h1>Clients</h1>
                  <ul>
                    {results.clients.map((r, i) => {
                      return (
                        <li key={i}>
                          <a href={`/clients/edit/${r.client_id}`}>{r.name}</a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </SearchResult>
            <SearchResult>
              {results.funders.length > 0 && (
                <div>
                  <h1>Funders</h1>
                  <ul>
                    {results.funders.map((r, i) => {
                      return (
                        <li key={i}>
                          <a href={`/funders/edit/${r.id}`}>{r.name}</a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </SearchResult>
            <SearchResult>
              {results.contacts.length > 0 && (
                <div>
                  <h1>Contacts</h1>
                  <ul>
                    {results.contacts.map((r, i) => {
                      return (
                        <li key={i}>
                          <a href={`/contacts/edit/${r.id}`}>
                            {r.first_name} {r.last_name}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </SearchResult>
            <SearchResult>
              {results.vault.length > 0 &&
                permissions.includes('read-vault') && (
                  <div>
                    <h1>Vault</h1>
                    <ul>
                      {results.vault.map((r, i) => {
                        return (
                          <li key={i}>
                            <a href={`/vault/${r.id}/general-information`}>
                              {r.name}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
            </SearchResult>
            {results.total == 0 && (
              <SearchResult>
                <div>
                  <h1>No Results</h1>
                </div>
              </SearchResult>
            )}
          </Loadable>
        </SearchWrapper>
      )}
    </Flex>
  );
};

export default UniversalSearch;
